import { useState, useEffect, useRef } from "react"
import { Link } from "react-router"
import { fetchMeCodes, listCodeGroups } from "../../lib/api"
import CodeRow from "../CodeRow"
import RecentlyClaimedSection from "../RecentlyClaimedSection"
import EditCodeModal from "../EditCodeModal"
import Pagination from "./Pagination"
import GroupsDialog from "./GroupsDialog"
import SelectionBar from "./SelectionBar"
import RepointDialog from "./RepointDialog"
import CreateManyDialog from "./CreateManyDialog"
import { scanLimitState, PastLimitText, NearLimitText } from "./PlanCard"
import FoundCodesCard from "./FoundCodesCard"

const REVIEW_WINDOW_MS = 30 * 24 * 60 * 60 * 1000
const SEARCH_DEBOUNCE_MS = 250
const VIEW_KEY = "tango_codes_view"

// A code the "Recently saved to your account" box lists: claimed in the last
// 30 days and not yet acknowledged.
function awaitingReview(code) {
  return code.claimed_at && !code.claim_acknowledged_at &&
    new Date(code.claimed_at).getTime() > Date.now() - REVIEW_WINDOW_MS
}

const SORTS = [
  { value: "top", label: "Top" },
  { value: "new", label: "New" },
  { value: "az", label: "A-Z" },
]

// YourCodesCard owns the code list: server-side search + sort (so the count and
// ordering stay correct across pagination), a list/grid toggle persisted in
// localStorage, and the edit-code modal. The parent provides the optimistic
// hooks: `newCode` (builder create → prepend, or restyle → patch in place) and
// row-level callbacks (claim ack, disclaim, upgrade). On a modal save the row
// is patched in place locally and bubbled up via onCodeUpdated.
//
// Groups (docs/plans/2026-09-15-code-groups.plan.md): a group filter beside
// search, a Groups dialog to create / rename / delete, and a Select mode that
// moves one code, a page, or every code matching the current view into a
// group. Selection is list-view only; choosing Select switches to the list.
export default function YourCodesCard({
  isPaid,
  // BulkRepoint flag, per user, from /api/v1/me. The server enforces it; this
  // only hides the Change destination action while it's off.
  bulkRepoint = false,
  bulkCreate = false,
  // Making a hosted contact page (#332), per user from /api/v1/me.
  contactPages = false,
  onCodesLoaded,
  onDisclaim,
  onAcknowledgeAll,
  onUpgrade,
  onCodeUpdated,
  // Codes were added, moved or repointed, so the stat cards above may be
  // stale (#327). Disclaim and edits already tell the parent their own way.
  onLibraryChanged,
  newCode,
  highlight,
  openEditToken,
}) {
  const [page, setPage]     = useState(1)
  const [q, setQ]           = useState("")
  const [qParam, setQParam] = useState("")
  const [sort, setSort]     = useState("new")
  const [view, setView]     = useState(() =>
    (typeof localStorage !== "undefined" && localStorage.getItem(VIEW_KEY) === "grid") ? "grid" : "list"
  )
  const [data, setData]     = useState(null) // null while loading
  const [error, setError]   = useState(null)
  const [editing, setEditing] = useState(null)
  const [group, setGroup]   = useState("")        // "" = all, "ungrouped", or a group id
  const [groupData, setGroupData] = useState(null) // { groups, ungrouped_count, total }
  const [groupsOpen, setGroupsOpen] = useState(false)
  const [selecting, setSelecting] = useState(false)
  const [selection, setSelection] = useState({ all: false, tokens: new Set() })
  const [repointing, setRepointing] = useState(false)
  const [creatingMany, setCreatingMany] = useState(false)
  const [notice, setNotice] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [flashToken, setFlashToken] = useState(null)
  const cardRef = useRef(null)

  // Debounce the search box into qParam (which drives the fetch), resetting to
  // page 1 whenever the query settles on a new value.
  useEffect(() => {
    const t = setTimeout(() => {
      setQParam((prev) => {
        if (prev !== q) setPage(1)
        return q
      })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => {
    if (typeof localStorage !== "undefined") localStorage.setItem(VIEW_KEY, view)
  }, [view])

  function loadGroups() {
    return listCodeGroups()
      .then((payload) => setGroupData(payload))
      .catch(() => {}) // the list still works ungrouped; the filter just stays empty
  }
  useEffect(() => { loadGroups() }, [])

  // A change of search or filter changes what "all matching" means, so an
  // all-matching selection can't survive it; an explicit pick of tokens can.
  useEffect(() => {
    setSelection((s) => (s.all ? { all: false, tokens: new Set() } : s))
  }, [qParam, group])

  useEffect(() => {
    let cancelled = false
    fetchMeCodes({ page, per: 10, q: qParam, sort, group })
      .then((payload) => {
        if (cancelled) return
        setData(payload)
        setError(null)
        onCodesLoaded?.(payload)
      })
      .catch((e) => {
        if (cancelled) return
        setError(e.message || "Couldn't load your codes.")
      })
    return () => { cancelled = true }
  }, [page, qParam, sort, group, reloadKey])

  // The filter counts come from their own endpoint, so anything that changes
  // the library without going through the Groups dialog — a builder create, a
  // disclaim, a delete — leaves them stale for the rest of the session. The
  // list's own unfiltered total is the cheap tell: when it disagrees with the
  // total the counts were built from, refetch them. The ref bounds it to one
  // refetch per distinct total, so a genuine disagreement can't loop.
  const reconciledTotalRef = useRef(null)
  useEffect(() => {
    if (!data || !groupData) return
    if (data.total_unfiltered === groupData.total) return
    if (reconciledTotalRef.current === data.total_unfiltered) return
    reconciledTotalRef.current = data.total_unfiltered
    loadGroups()
  }, [data, groupData])

  // Builder publishes on create AND on subsequent restyle. Prepend a brand-new
  // code; patch an existing one in place. Only meaningful on the unfiltered
  // first page in default order — other views reconcile on the next fetch.
  useEffect(() => {
    if (!newCode || page !== 1 || qParam || group || sort !== "new" || !data) return
    setData((d) => {
      const exists = d.codes.some((c) => c.token === newCode.token)
      if (exists) {
        return { ...d, codes: d.codes.map((c) => (c.token === newCode.token ? { ...c, ...newCode } : c)) }
      }
      // Count it, don't just show it: the "N of M" header reads these, and the
      // group counts reconcile off total_unfiltered moving.
      return { ...d, codes: [ newCode, ...d.codes ], total: d.total + 1, total_unfiltered: d.total_unfiltered + 1 }
    })
  }, [newCode])

  // The builder's Done (#316): bring the code into view and mark it for a few
  // seconds, so the person sees where it went. On the default view a new code
  // is first in the list (the prepend above); on any other view the card
  // itself comes into view and the notice still says it saved.
  useEffect(() => {
    if (!highlight?.token) return
    setFlashToken(highlight.token)
    setNotice("Code saved.")
    const raf = requestAnimationFrame(() => {
      const root = cardRef.current
      const row = root?.querySelector(`[data-token="${CSS.escape(highlight.token)}"]`)
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      ;(row || root)?.scrollIntoView({ block: row ? "center" : "start", behavior: reduceMotion ? "auto" : "smooth" })
    })
    const t = setTimeout(() => {
      setFlashToken(null)
      setNotice((n) => (n === "Code saved." ? null : n))
    }, 4000)
    return () => { cancelAnimationFrame(raf); clearTimeout(t) }
  }, [highlight])

  // Deep-link from an anonymous hero upgrade (BillingController set ?code=…,
  // Dashboard forwarded it): once the list has loaded, open that code's editor
  // so the visitor lands back where the Pro lock stopped them — now owned, Pro,
  // shapes unlocked. The freshly claimed code is newest, so it's on page 1 of
  // the default "new" sort; fires once.
  const openedEditRef = useRef(false)
  useEffect(() => {
    if (!openEditToken || openedEditRef.current || !data) return
    const code = data.codes.find((c) => c.token === openEditToken)
    if (code) {
      openedEditRef.current = true
      setEditing(code)
    }
  }, [openEditToken, data])

  function handleSaved(updated) {
    setData((d) => (d ? { ...d, codes: d.codes.map((c) => (c.token === updated.token ? { ...c, ...updated } : c)) } : d))
    onCodeUpdated?.(updated)
  }

  function changeSort(value) {
    setSort(value)
    setPage(1)
  }

  function changeGroup(value) {
    setGroup(value)
    setPage(1)
  }

  // "Yes, all mine": the server marks every unreviewed claim acknowledged, so
  // mark the ones on this page too and the box unmounts at once (#317). Other
  // pages read the server's answer when they load.
  async function handleAcknowledgeAll() {
    const count = data?.codes.filter(awaitingReview).length || 0
    if (!(await onAcknowledgeAll?.())) return
    const at = new Date().toISOString()
    setData((d) => d && {
      ...d,
      codes: d.codes.map((c) => (awaitingReview(c) ? { ...c, claim_acknowledged_at: at } : c)),
    })
    setNotice(count === 1 ? "Done. This code is yours." : "Done. These codes are yours.")
  }

  // Codes made with this address on another device (#329), added by the
  // owner's own press: they join the list, and the stat cards count them.
  function handleFoundAdded(count) {
    setNotice(
      count === 0 ? "Nothing added. Those codes can't be added any more." :
      count === 1 ? "Added. This code is yours now." :
                    `Added ${count} codes. They're yours now.`
    )
    setReloadKey((k) => k + 1)
    onCodeUpdated?.()
  }

  // "Not mine, remove" takes the code off this account, so it leaves the list.
  async function handleDisclaim(token) {
    if (!(await onDisclaim?.(token))) return
    setNotice("Removed from your account.")
    setReloadKey((k) => k + 1)
  }

  function startSelecting() {
    setSelecting(true)
    setView("list")
    setNotice(null)
  }

  function stopSelecting() {
    setSelecting(false)
    setSelection({ all: false, tokens: new Set() })
  }

  // Both toggles normalise `all` to the explicit set it implies for this page
  // first, then toggle. Dropping out of all-matching is deliberate (there's no
  // way to say "all matching except these" to the server) and the bar's count
  // changes to show it — but it has to be a real toggle either way.
  function toggleSelect(code) {
    setSelection((s) => {
      const tokens = new Set(s.all ? data.codes.map((c) => c.token) : s.tokens)
      tokens.has(code.token) ? tokens.delete(code.token) : tokens.add(code.token)
      return { all: false, tokens }
    })
  }

  function togglePage() {
    const pageTokens = data.codes.map((c) => c.token)
    setSelection((s) => {
      // Seeding from [] while `all` was set re-added the whole page, so the
      // checked box stayed checked and 153-selected silently became 10.
      const tokens = new Set(s.all ? pageTokens : s.tokens)
      const allOnPage = pageTokens.every((t) => tokens.has(t))
      pageTokens.forEach((t) => (allOnPage ? tokens.delete(t) : tokens.add(t)))
      return { all: false, tokens }
    })
  }

  // Groups changed (dialog) or codes moved: refresh the filter counts, and if
  // the active filter no longer exists, fall back to all codes. Never throws —
  // a failed count refresh must still let the list below reload, or the rows
  // keep showing the group they were just moved out of.
  async function refreshGroups() {
    try {
      const payload = await listCodeGroups()
      setGroupData(payload)
      // "Ungrouped" is only an option while at least one group exists
      // (GroupFilter hides it otherwise), so deleting the last group strands
      // it: the <select> falls back to showing "All codes" while the list
      // stays filtered.
      const stillOffered =
        group === "" ||
        (group === "ungrouped" ? payload.groups.length > 0
                               : payload.groups.some((g) => String(g.id) === String(group)))
      if (!stillOffered) {
        setGroup("")
        setPage(1)
      }
    } catch {
      // Counts may be stale; the reconcile effect above picks them up later.
    } finally {
      reloadAfterChange()
    }
  }

  // The rows changed under the list (a move, a repoint, a batch of new
  // codes): reload them, and tell the parent so the stat cards follow.
  function reloadAfterChange() {
    setReloadKey((k) => k + 1)
    onLibraryChanged?.()
  }

  // `groupName` is null when the codes were taken out of every group.
  function handleMoved({ moved, matched, groupName }) {
    stopSelecting()
    const noun = (n) => `${n.toLocaleString()} ${n === 1 ? "code" : "codes"}`
    const already = matched - moved
    const done = groupName ? `Moved ${noun(moved)} to ${groupName}.` : `Took ${noun(moved)} out of their groups.`
    setNotice(already > 0 ? `${done} ${noun(already)} ${already === 1 ? "was" : "were"} already there.` : done)
    refreshGroups()
  }

  // The dialog already told the user what changed (and offered Undo), so this
  // only drops out of Select mode and reloads the rows to show their new
  // destinations.
  function handleRepointed() {
    stopSelecting()
    reloadAfterChange()
  }

  if (error) {
    return <div className="bg-white dark:bg-midnight border border-fail rounded-lg p-6 text-fail text-sm">{error}</div>
  }
  if (!data) {
    return (
      <div className="bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg p-6 text-center text-sm text-[color:var(--color-muted)] dark:text-muted-light">
        Loading your codes…
      </div>
    )
  }

  const recentlyClaimed = data.codes.filter(awaitingReview)

  return (
    <div ref={cardRef} className="scroll-mt-4 bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg p-4">
      <div className="flex flex-col gap-3 mb-3 min-[640px]:flex-row min-[640px]:items-center min-[640px]:justify-between">
        <div className="flex items-baseline gap-2">
          <h2 className="font-display font-bold text-lg text-ink dark:text-cloud">Your codes</h2>
          <span className="text-xs text-[color:var(--color-muted)] dark:text-muted-light font-mono">
            {data.total} of {data.total_unfiltered}
          </span>
        </div>
        {/* Controls: search grows to fill the row on mobile; sort + view sit
            beside it and wrap rather than overflowing the card. */}
        <div className="flex items-center flex-wrap gap-2">
          <SearchBox value={q} onChange={setQ} />
          <GroupFilter value={group} groupData={groupData} onChange={changeGroup} onManage={() => setGroupsOpen(true)} />
          <Segmented options={SORTS} value={sort} onChange={changeSort} ariaLabel="Sort codes" />
          <ViewToggle view={view} onChange={(v) => { if (v === "grid") stopSelecting(); setView(v) }} />
          {bulkCreate && !selecting && (
            <button type="button" onClick={() => setCreatingMany(true)}
              className="px-3 py-2 text-sm font-medium rounded border border-[color:var(--color-border)] dark:border-storm text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud hover:border-voltage transition">
              Create many
            </button>
          )}
          {!selecting && data.codes.length > 0 && (
            <button type="button" onClick={startSelecting}
              className="px-3 py-2 text-sm font-medium rounded border border-[color:var(--color-border)] dark:border-storm text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud hover:border-voltage transition">
              Select
            </button>
          )}
        </div>
      </div>

      {notice && !selecting && (
        <div role="status" className="mb-3 flex items-center justify-between gap-3 text-sm px-3 py-2 rounded border border-voltage/50 bg-voltage-pale dark:bg-voltage/10 text-ink dark:text-cloud">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud">×</button>
        </div>
      )}

      {selecting && (
        <SelectionBar
          selection={selection}
          pageTokens={data.codes.map((c) => c.token)}
          total={data.total}
          filter={{ q: qParam, group }}
          groups={groupData?.groups || []}
          onSelectAll={() => setSelection({ all: true, tokens: new Set() })}
          onClear={stopSelecting}
          onMoved={handleMoved}
          onRepoint={bulkRepoint ? () => setRepointing(true) : null}
        />
      )}

      {repointing && bulkRepoint && (
        <RepointDialog
          selection={selection}
          count={selection.all ? data.total : selection.tokens.size}
          filter={{ q: qParam, group }}
          onClose={() => setRepointing(false)}
          onDone={handleRepointed}
          onUpgrade={onUpgrade}
        />
      )}

      {creatingMany && bulkCreate && (
        <CreateManyDialog
          groups={groupData?.groups || []}
          onClose={() => setCreatingMany(false)}
          // New rows, and possibly new folders: refresh both.
          onDone={refreshGroups}
        />
      )}

      <FoundCodesCard onAdded={handleFoundAdded} onDismissed={setNotice} />

      {recentlyClaimed.length > 0 && (
        <div className="mb-3">
          <RecentlyClaimedSection codes={recentlyClaimed} onDisclaim={handleDisclaim} onAcknowledgeAll={handleAcknowledgeAll} />
        </div>
      )}

      {!isPaid && <ScanLimitNotices codes={data.codes} onUpgrade={onUpgrade} />}

      {data.codes.length === 0 ? (
        <div className="text-center text-sm text-[color:var(--color-muted)] dark:text-muted-light py-8">
          {qParam
            ? `No codes match “${qParam}”.`
            : group
              ? "No codes in this group yet. Select codes to move them here."
              : "No codes yet. Make your first one above to get started."}
        </div>
      ) : view === "grid" ? (
        <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}>
          {data.codes.map((code) => (
            <CodeGridCard key={code.token} code={code} onEdit={setEditing} highlighted={flashToken === code.token} />
          ))}
        </div>
      ) : (
        // No overflow-hidden: it clipped the row menu, so on the last row
        // "Edit code" was out of reach (#334). CodeRow rounds its own corners.
        <div className="border border-[color:var(--color-border)] dark:border-storm rounded-lg">
          {selecting && (
            <label className="flex items-center gap-3 px-4 py-2 border-b border-[color:var(--color-border)] dark:border-storm text-xs text-[color:var(--color-muted)] dark:text-muted-light cursor-pointer">
              <input
                type="checkbox"
                checked={selection.all || data.codes.every((c) => selection.tokens.has(c.token))}
                onChange={togglePage}
                className="w-4 h-4 accent-[color:var(--color-voltage)] cursor-pointer"
              />
              Select this page
            </label>
          )}
          {data.codes.map((code) => (
            <CodeRow
              key={code.token}
              code={code}
              onEdit={setEditing}
              highlighted={flashToken === code.token}
              selectable={selecting}
              selected={selection.all || selection.tokens.has(code.token)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

      <Pagination page={data.page} totalPages={data.total_pages} onChange={setPage} />

      {groupsOpen && (
        <GroupsDialog
          groups={groupData?.groups || []}
          onClose={() => setGroupsOpen(false)}
          onChanged={refreshGroups}
        />
      )}

      {editing && (
        <EditCodeModal
          code={editing}
          isPaid={isPaid}
          contactPages={contactPages}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
          onUpgrade={onUpgrade}
        />
      )}
    </div>
  )
}

// The free plan's limit notices above the list (#327). A code past its free
// scans is locked and says so, with the way forward; a code close to it is
// not locked yet and says how close. One or two codes are named, as the list
// names them. Counts are for the codes on this page. Never shown on Pro.
function ScanLimitNotices({ codes, onUpgrade }) {
  const past = codes.filter((c) => scanLimitState(c) === "past")
  const near = codes.filter((c) => scanLimitState(c) === "near")
  if (past.length === 0 && near.length === 0) return null

  return (
    <>
      {past.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-fail font-medium px-3 py-2 border border-fail/30 bg-fail/5 rounded">
          <span className="break-words min-w-0"><PastLimitText codes={past} /></span>
          <button
            type="button"
            onClick={onUpgrade}
            className="flex-shrink-0 font-semibold text-plasma dark:text-plasma-light underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
          >
            {past.length === 1 ? "Upgrade to edit it" : "Upgrade to edit them"}
          </button>
        </div>
      )}
      {near.length > 0 && (
        <div className="mb-3 text-xs text-ink dark:text-cloud font-medium px-3 py-2 border border-warn/50 bg-warn/10 rounded break-words">
          <NearLimitText codes={near} />
        </div>
      )}
    </>
  )
}

// The group filter: All codes, each group, Ungrouped, with counts, and a way
// into the Groups dialog. A native <select> so it's keyboard- and
// screen-reader-correct without a custom listbox.
function GroupFilter({ value, groupData, onChange, onManage }) {
  const groups = groupData?.groups || []
  const MANAGE = "__manage__"
  return (
    <>
      <label className="sr-only" htmlFor="code-group-filter">Filter by group</label>
      <select
        id="code-group-filter"
        value={value}
        onChange={(e) => (e.target.value === MANAGE ? onManage() : onChange(e.target.value))}
        className="max-w-[180px] border border-[color:var(--color-border)] dark:border-storm rounded px-2.5 py-2 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 dark:focus:ring-offset-midnight"
      >
        <option value="">All codes{groupData ? ` (${groupData.total})` : ""}</option>
        {groups.map((g) => <option key={g.id} value={String(g.id)}>{g.name} ({g.code_count})</option>)}
        {groups.length > 0 && <option value="ungrouped">Ungrouped ({groupData.ungrouped_count})</option>}
        <option value={MANAGE}>{groups.length > 0 ? "Manage groups…" : "Create a group…"}</option>
      </select>
    </>
  )
}

function SearchBox({ value, onChange }) {
  return (
    <div className="relative flex-1 min-w-[140px] min-[640px]:flex-none">
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
           className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--color-muted)] dark:text-muted-light pointer-events-none">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search codes"
        aria-label="Search codes"
        className="w-full min-[640px]:w-[180px] min-[640px]:focus:w-[210px] transition-[width] border border-[color:var(--color-border)] dark:border-storm rounded pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 dark:focus:ring-offset-midnight focus:border-voltage"
      />
    </div>
  )
}

function Segmented({ options, value, onChange, ariaLabel }) {
  return (
    <div className="inline-flex rounded border border-[color:var(--color-border)] dark:border-storm overflow-hidden" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={
            "px-3 py-2 text-sm font-medium transition border-l first:border-l-0 border-[color:var(--color-border)] dark:border-storm " +
            (value === o.value ? "bg-voltage-pale text-voltage-deep dark:bg-voltage/15" : "bg-white dark:bg-midnight text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function ViewToggle({ view, onChange }) {
  return (
    <div className="inline-flex rounded border border-[color:var(--color-border)] dark:border-storm overflow-hidden" role="group" aria-label="View">
      <ToggleBtn active={view === "list"} onClick={() => onChange("list")} label="List view">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
      </ToggleBtn>
      <ToggleBtn active={view === "grid"} onClick={() => onChange("grid")} label="Grid view">
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
      </ToggleBtn>
    </div>
  )
}

function ToggleBtn({ active, onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={
        "px-2.5 py-2 transition border-l first:border-l-0 border-[color:var(--color-border)] dark:border-storm " +
        (active ? "bg-voltage-pale text-voltage-deep dark:bg-voltage/15" : "bg-white dark:bg-midnight text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud")
      }
    >
      {children}
    </button>
  )
}

// Grid variant of a code: a square QR well over a compact body. Shares the
// copy / edit / analytics affordances with the list row.
function CodeGridCard({ code, onEdit, highlighted = false }) {
  const [copied, setCopied] = useState(false)
  const locked = code.editable === false
  const shortDisplay = (code.short_url || `tgo.sh/${code.token}`).replace(/^https?:\/\//, "")

  function copyShortLink() {
    navigator.clipboard?.writeText(code.short_url || `tgo.sh/${code.token}`).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1400)
  }

  return (
    <div data-token={code.token} className={"border rounded-lg overflow-hidden hover:border-voltage hover:shadow-sm transition " + (highlighted ? "border-voltage ring-2 ring-voltage/40" : "border-[color:var(--color-border)] dark:border-storm")}>
      <div className="p-[22px] border-b border-[color:var(--color-border)] dark:border-storm flex items-center justify-center" style={{ background: code.background_color || "#FFFFFF" }}>
        <img src={code.image_url} alt={`QR code for ${shortDisplay}`} className="w-full max-w-[120px] aspect-square" />
      </div>
      <div className="p-3">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm text-ink dark:text-cloud truncate" title={code.display_label}>{code.display_label}</span>
          {locked && (
            <span className="text-[color:var(--color-muted)] dark:text-muted-light flex-shrink-0" title="Locked · upgrade to edit" aria-label="Editing locked">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            </span>
          )}
        </div>
        <div className="font-mono text-xs text-voltage-dark dark:text-voltage-light truncate mt-0.5">{shortDisplay}</div>
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">
            <span className="font-mono text-sm text-ink dark:text-cloud">{(code.scan_count || 0).toLocaleString()}</span> scans
          </div>
          <div className="flex gap-1.5">
            <GridIconBtn onClick={copyShortLink} label="Copy short link" active={copied}>
              {copied
                ? <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                : <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>}
            </GridIconBtn>
            <GridIconBtn onClick={() => onEdit?.(code)} label="Edit code">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
            </GridIconBtn>
            <Link to={`/app/codes/${code.token}`} aria-label="View analytics" title="View analytics"
              className="w-[30px] h-[30px] inline-flex items-center justify-center border border-[color:var(--color-border)] dark:border-storm rounded text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud hover:border-voltage hover:bg-voltage-pale dark:hover:bg-voltage/15 transition">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

function GridIconBtn({ onClick, label, active = false, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={
        "w-[30px] h-[30px] inline-flex items-center justify-center border rounded bg-transparent cursor-pointer transition " +
        (active
          ? "border-voltage text-voltage-dark dark:text-voltage-light bg-voltage-pale dark:bg-voltage/15"
          : "border-[color:var(--color-border)] dark:border-storm text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud hover:border-voltage hover:bg-voltage-pale dark:hover:bg-voltage/15")
      }
    >
      {children}
    </button>
  )
}
